import React from 'react';
import { useFormat } from '../../utils';
import { Badge } from '@shared/ui/badge';
import { Camera, Clock, Users } from 'lucide-react';

const MobilePreview = () => {
  const { state } = useFormat();
  const selectedFields = state.selectedFields;

  const getUtilityColor = () => {
    const utilityService = state.selectedField?.utilityService || 'water';
    switch (utilityService) {
      case 'water':
        return 'bg-blue-500 text-white';
      case 'electric':
        return 'bg-yellow-300 text-gray-900';
      case 'gas':
        return 'bg-red-500 text-white';
      default:
        return 'bg-gray-200 text-gray-900';
    }
  };

  const getInputType = (fieldType: string) => {
    switch (fieldType) {
      case 'numeric':
      case 'float':
        return 'number';
      case 'date':
        return 'date';
      case 'time':
        return 'time';
      case 'PHOTO':
        return 'photo';
      case 'DROPDOWN':
        return 'dropdown';
      default:
        return 'text';
    }
  };

  const renderMockInput = (fieldType: string) => {
    const inputType = getInputType(fieldType);

    if (inputType === 'photo') {
      return (
        <div className="border border-dashed border-gray-300 rounded-md p-4 text-center">
          <div className="text-gray-500 mb-2">
            <CameraIcon />
          </div>
          <span className="text-sm text-gray-500">Tap to take photo</span>
        </div>
      );
    }
    
    if (inputType === 'dropdown') {
      return (
        <select className="w-full p-2 border border-gray-300 rounded-md bg-white">
          <option>Select...</option>
          <option>Option 1</option>
          <option>Option 2</option>
          <option>Option 3</option>
        </select>
      );
    }

    return (
      <input
        type={inputType}
        className="w-full p-2 border border-gray-300 rounded-md"
        placeholder={
          inputType === 'number'
            ? '0'
            : inputType === 'date'
            ? 'Select date'
            : 'Enter value'
        }
      />
    );
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-2">Mobile Preview</h3>

      <div className="flex flex-col items-center">
        {/* Device frame */}
        <div className="border-8 border-gray-800 rounded-3xl overflow-hidden w-full max-w-[280px] shadow-xl bg-white">
          {/* Status bar */}
          <div className="bg-gray-800 text-white text-xs p-1 flex justify-between">
            <span>10:42</span>
            <span>📶 📡 🔋</span>
          </div>

          {/* App content */}
          <div className="h-[500px] overflow-y-auto">
            {/* Header */}
            <div className={`p-3 sticky top-0 z-10 ${getUtilityColor()}`}>
              <h3 className="font-semibold text-center">
                {state.currentFormat?.name || 'New Format'}
              </h3>
            </div>

            {/* Form */}
            <div className="p-3 space-y-3">
              {selectedFields.length > 0 ? (
                selectedFields.map((field) => (
                  <div key={field.id} className="space-y-1">
                    <label className="block text-sm font-medium">
                      {field.fieldName}
                      {field.isRequired && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </label>
                    {renderMockInput(field.fieldType)}
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-6">
                  <p>Select fields to preview the form</p>
                </div>
              )}

              {/* Action buttons */}
              {selectedFields.length > 0 && (
                <div className="pt-4 flex space-x-2">
                  <button className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-md text-sm">
                    Submit Reading
                  </button>

                  {selectedFields.some(
                    (field) => field.fieldType === 'photo'
                  ) && (
                    <button className="bg-gray-200 text-gray-800 py-2 px-3 rounded-md">
                      <Camera size={16} />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CameraIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="mx-auto h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

export default MobilePreview;
